import { useState } from "react";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
	Form,
	FormControl,
	FormDescription,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Card } from "@/components/ui/card";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/providers/AuthProvider";

// Define the role type to match what's expected in AdminConsole
type UserRole = "user" | "admin" | "super_admin";

// Schema factory function that takes isSuperAdmin as parameter
const createUserFormSchema = (isSuperAdmin: boolean) => z.object({
	email: z.string().email({ message: "Please enter a valid email address" }),
	firstName: z.string().min(1, { message: "First name is required" }),
	lastName: z.string().min(1, { message: "Last name is required" }),
	role: z.enum(["user", "admin", "super_admin"] as const),
	companyId: z.string().optional().nullable(),
	createNewCompany: z.boolean().default(false),
	newCompanyName: z.string().optional()
		.refine(val => val === undefined || val === "" || val?.length >= 2, {
			message: "Company name must be at least 2 characters",
		}),
	acknowledgeCharges: z.boolean().refine(val => val === true, {
		message: "You must acknowledge the potential charges before proceeding",
	}),
}).refine(
	(data) => {
		// If user is not a super admin, no company validation needed
		if (!isSuperAdmin) {
			return true;
		}
		
		// Below validations only apply to super admins
		
		// If creating a new company, newCompanyName should be provided
		if (data.createNewCompany && (!data.newCompanyName || data.newCompanyName.length < 2)) {
			return false;
		}
		
		// For super_admin role, company ID is not required
		if (data.role === "super_admin") {
			return true;
		}
		
		// If not creating a new company and not a super_admin role, companyId should be provided
		if (!data.createNewCompany && !data.companyId) {
			return false;
		}
		
		return true;
	},
	{
		message: "Please either select an existing company or create a new one",
		path: ["companyId"],
	}
);

type UserFormValues = z.infer<ReturnType<typeof createUserFormSchema>>;

interface AddUserModalProps {
	isOpen: boolean;
	onClose: () => void;
	onSubmit: (data: UserFormValues) => void;
	organizations: { id: string; name: string }[];
}

export const AddUserModal = ({ isOpen, onClose, onSubmit, organizations }: AddUserModalProps) => {
	
	const { t } = useTranslation();
	const { isSuperAdmin } = useAuth();
	
	// Use the schema factory with isSuperAdmin value
	const userFormSchema = createUserFormSchema(isSuperAdmin);
	
	const form = useForm<UserFormValues>({
		resolver: zodResolver(userFormSchema),		defaultValues: {
			email: "",
			firstName: "",
			lastName: "",
			role: "user",
			companyId: "",
			createNewCompany: false,
			newCompanyName: "",
			acknowledgeCharges: false,
		},
	});

	const role = form.watch("role");
	const createNewCompany = form.watch("createNewCompany");

	const handleSubmit = (values: UserFormValues) => {
		//console.log("AddUserModal handleSubmit called with:", values);
		onSubmit(values);
		// We'll let the parent component manage closing based on success
	};

	// Add this debug function to check form errors
	const onError = (errors: any) => {
		console.error("Form validation errors:", errors);
	};

	return (
		<Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
			<DialogContent className="sm:max-w-[525px] max-h-[90vh] overflow-y-auto">
				<DialogHeader>
					<DialogTitle>{t("admin.modals.addUser.title")}</DialogTitle>
				</DialogHeader>

				<Form {...form}>
					<form onSubmit={form.handleSubmit(handleSubmit, onError)} className="space-y-6 py-4">
						{/* User Information */}
						<div className="grid grid-cols-2 gap-4">
							<FormField
								control={form.control}
								name="firstName"
								render={({ field }) => (
									<FormItem>
										<FormLabel>{t("admin.user.firstName")}</FormLabel>
										<FormControl>
											<Input placeholder="John" {...field} />
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>

							<FormField
								control={form.control}
								name="lastName"
								render={({ field }) => (
									<FormItem>
										<FormLabel>{t("admin.user.lastName")}</FormLabel>
										<FormControl>
											<Input placeholder="Doe" {...field} />
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>

						<FormField
							control={form.control}
							name="email"
							render={({ field }) => (
								<FormItem>
									<FormLabel>{t("common.email")}</FormLabel>
									<FormControl>
										<Input placeholder="user@example.com" {...field} />
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>

						<FormField
							control={form.control}
							name="role"
							render={({ field }) => (
								<FormItem>
									<FormLabel>{t("admin.users.role")}</FormLabel>
									<Select
										onValueChange={field.onChange}
										defaultValue={field.value}
									>
										<FormControl>
											<SelectTrigger>
												<SelectValue placeholder={t("admin.users.selectRole")} />
											</SelectTrigger>
										</FormControl>
										<SelectContent>
											<SelectItem value="user">{t("admin.users.userRole")}</SelectItem>
											<SelectItem value="admin">{t("admin.users.adminRole")}</SelectItem>
											{isSuperAdmin && (
												<SelectItem value="super_admin">{t("admin.users.superAdminRole")}</SelectItem>
											)}
										</SelectContent>
									</Select>
									<FormDescription>
										{role === "super_admin"
											? t("admin.users.superAdminDesc")
											: role === "admin"
												? t("admin.users.adminDesc")
												: t("admin.users.userDesc")}
									</FormDescription>
									<FormMessage />
								</FormItem>							)}
						/>

						{/* Charges Acknowledgment */}
						<FormField
							control={form.control}
							name="acknowledgeCharges"
							render={({ field }) => (
								<FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-sm border p-4">
									<FormControl>
										<Checkbox
											checked={field.value}
											onCheckedChange={field.onChange}
										/>
									</FormControl>									<div className="space-y-1 leading-none">
										<FormLabel className="text-sm font-medium">
											{t("admin.users.acknowledgeCharges")}
										</FormLabel>
										<FormDescription className="text-xs text-muted-foreground">
											{t("admin.users.acknowledgeChargesDesc")}
										</FormDescription>
									</div>
									<FormMessage />
								</FormItem>
							)}
						/>

						{isSuperAdmin && (
							<Card className="p-4">
								<FormField
									control={form.control}
									name="createNewCompany"
									render={({ field }) => (
										<FormItem className="flex flex-row items-start space-x-3 space-y-0">
											<FormControl>
												<Checkbox
													checked={field.value}
													onCheckedChange={field.onChange}
												/>
											</FormControl>
											<div className="space-y-1 leading-none">
												<FormLabel>{t("admin.users.createNewCompany")}</FormLabel>
												<FormDescription>
													{t("admin.users.createNewCompanyDesc")}
												</FormDescription>
											</div>
										</FormItem>
									)}
								/>

								{createNewCompany ? (
									<FormField
										control={form.control}
										name="newCompanyName"
										render={({ field }) => (
											<FormItem className="mt-4">
												<FormLabel>{t("admin.users.newCompanyName")}</FormLabel>
												<FormControl>
													<Input placeholder="Acme Inc." {...field} />
												</FormControl>
												<FormMessage />
											</FormItem>
										)}
									/>
								) : (
									<FormField
										control={form.control}
										name="companyId"
										render={({ field }) => (
											<FormItem className="mt-4">
												<FormLabel>{t("admin.users.selectCompany")}</FormLabel>
												<Select
													onValueChange={field.onChange}
													defaultValue={field.value || undefined}
												>
													<FormControl>
														<SelectTrigger>
															<SelectValue placeholder={t("admin.users.selectCompanyPlaceholder")} />
														</SelectTrigger>
													</FormControl>
													<SelectContent>
														{organizations.map((company) => (
															<SelectItem key={company.id} value={company.id}>
																{company.name}
															</SelectItem>
														))}
													</SelectContent>
												</Select>
												<FormMessage />
											</FormItem>
										)}
									/>
								)}
							</Card>
						)}

						<div className="flex justify-end space-x-3 pt-4">
							<Button type="button" variant="outline" onClick={onClose}>
								{t("common.cancel")}
							</Button>
							<Button 
								type="button" 
								onClick={() => {
									//console.log("Submit button clicked");
									form.handleSubmit((values) => {
										//console.log("Form submitted with values:", values);
										onSubmit(values);
									}, (errors) => {
										console.error("Form validation errors on direct submit:", errors);
									})();
								}}
							>
								{t("admin.users.createUser")}
							</Button>
						</div>
					</form>
				</Form>
			</DialogContent>
		</Dialog>
	);
};